import { Component, Input } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StudyShare, StudyShareService, getShareDisplayStatus } from '../../../services/study-share.service';

@Component({
  selector: 'app-study-share-history',
  templateUrl: './study-share-history.component.html',
  styleUrls: ['./study-share-history.component.scss'],
  standalone: false,
})
export class StudyShareHistoryComponent {
  @Input() shares: StudyShare[] = [];

  pendingRequestIds = new Set<string>();
  expandedShareId: string | null = null;

  constructor(
    private studyShareService: StudyShareService,
    private snackBar: MatSnackBar,
  ) {}

  get historicalShares(): StudyShare[] {
    return this.shares.filter(s =>
      s.status === 'expired' ||
      s.status === 'deleted' ||
      s.status === 'deletion_failed' ||
      s.status === 're_requested' ||
      s.status === 'reshared' ||
      s.status === 'rejected' ||
      s.status === 'uploaded_to_pacs' ||
      getShareDisplayStatus(s) === 'expired'
    );
  }

  canRequestReshare(share: StudyShare): boolean {
    return share.status === 'expired' ||
           share.status === 'deleted' ||
           share.status === 'deletion_failed' ||
           share.status === 'rejected' ||
           getShareDisplayStatus(share) === 'expired';
  }

  statusLabel(share: StudyShare): string {
    if (share.status === 'uploaded_to_pacs') return 'Uploaded to PACS';
    if (getShareDisplayStatus(share) === 'expired') return 'Expired';
    const map: Record<string, string> = {
      deleted: 'Deleted',
      deletion_failed: 'Deletion Failed',
      re_requested: 'Re-share Requested',
      reshared: 'Approved',
      rejected: 'Rejected',
    };
    return map[share.status] || share.status;
  }

  statusClass(share: StudyShare): string {
    if (share.status === 'uploaded_to_pacs') return 'status--pacs';
    if (getShareDisplayStatus(share) === 'expired') return 'status--expired';
    const map: Record<string, string> = {
      deleted: 'status--deleted',
      deletion_failed: 'status--failed',
      re_requested: 'status--pending',
      reshared: 'status--approved',
      rejected: 'status--rejected',
    };
    return map[share.status] || '';
  }

  toggleDetails(shareId: string): void {
    this.expandedShareId = this.expandedShareId === shareId ? null : shareId;
  }

  formatDate(ts: { toDate(): Date } | undefined): string {
    if (!ts) return '—';
    return ts.toDate().toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  formatExpiredAt(share: StudyShare): string {
    return this.formatDate(share.expired_at || share.effective_expires_at);
  }

  onRequestReshare(share: StudyShare): void {
    if (this.pendingRequestIds.has(share.id)) return;
    this.pendingRequestIds.add(share.id);

    this.studyShareService.requestReshare(share.id)
      .then(result => {
        this.snackBar.open(result.message, 'OK', { duration: 5000 });
      })
      .catch(err => {
        console.error('[StudyShareHistory] requestReshare failed:', err);
        this.snackBar.open('Failed to submit re-share request. Please try again.', 'Dismiss', { duration: 6000 });
      })
      .finally(() => {
        this.pendingRequestIds.delete(share.id);
      });
  }
}

